import { useEffect, useRef, useCallback } from 'react'

export interface WaveDashedLineProps {
  lineHeight?: number
  baseDashWidth?: number
  maxWaveWidth?: number
  dashHeight?: number
  dashGap?: number
  /** Width of the single wave pulse in dashes (default 12). */
  pulseWidth?: number
  dashColor?: string
}

export function WaveDashedLine({
  lineHeight = 300,
  baseDashWidth = 4,
  maxWaveWidth = 20,
  dashHeight = 2,
  dashGap = 3,
  pulseWidth = 12,
  dashColor = '#d97706',
}: WaveDashedLineProps) {
  const step = dashHeight + dashGap
  const dashCount = Math.max(1, Math.floor(lineHeight / step))
  const usableHeight = dashCount * step - dashGap
  const verticalPadding = (lineHeight - usableHeight) / 2
  // Only the left half of each expanding dash is visible; the right half is
  // cut off by the viewport edge, giving a flat right edge and wavy left edge.
  const containerWidth = maxWaveWidth / 2

  const containerRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)
  const propsRef = useRef({
    baseDashWidth,
    maxWaveWidth,
    pulseWidth,
    dashColor,
    dashCount,
  })
  propsRef.current = {
    baseDashWidth,
    maxWaveWidth,
    pulseWidth,
    dashColor,
    dashCount,
  }

  const animate = useCallback(() => {
    const container = containerRef.current
    const inner = innerRef.current
    if (!container || !inner) {
      rafRef.current = null
      return
    }

    const rect = container.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const inViewport = rect.top < viewportHeight && rect.bottom > 0
    const doc = document.documentElement
    const scrollable = doc.scrollHeight - doc.clientHeight

    const dashes = inner.children
    const count = propsRef.current.dashCount

    if (!inViewport || scrollable <= 0) {
      for (let i = 0; i < count; i++) {
        const el = dashes[i] as HTMLElement
        if (el) el.style.setProperty('--scaleX', '1')
      }
      rafRef.current = null
      return
    }

    // Scroll-linked progress from 0 (top of page) to 1 (bottom of page).
    const progress = Math.max(0, Math.min(1, window.scrollY / scrollable))

    const { baseDashWidth, maxWaveWidth, pulseWidth, dashColor } =
      propsRef.current

    // Map progress to the dash index the single peak should sit on.
    // 0 places the peak at the top dash; 1 places it at the bottom dash.
    const peakIndex = progress * (count - 1)

    for (let i = 0; i < count; i++) {
      const el = dashes[i] as HTMLElement
      if (!el) continue

      // Distance from the current dash to the moving peak, in dash indices.
      const distance = (i - peakIndex) / pulseWidth

      // A single non-repeating pulse. `bell` is 1.0 at the peak and falls off
      // smoothly towards both edges; it never repeats.
      const bell = Math.exp(-Math.PI * distance * distance)

      const scaleX = 1 + (maxWaveWidth / baseDashWidth - 1) * bell
      el.style.setProperty('--scaleX', String(scaleX))
      el.style.backgroundColor = dashColor
    }

    rafRef.current = null
  }, [])

  const schedule = useCallback(() => {
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(animate)
    }
  }, [animate])

  useEffect(() => {
    const handleScroll = () => schedule()
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', schedule, { passive: true })
    schedule()
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', schedule)
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
      }
      rafRef.current = null
    }
  }, [schedule])

  return (
    <div
      ref={containerRef}
      className="fixed right-0 top-1/2 z-[100] hidden -translate-y-1/2 md:block"
      style={{
        pointerEvents: 'none',
        width: containerWidth,
        height: lineHeight,
        overflow: 'hidden',
      }}
      role="scrollbar"
      aria-orientation="vertical"
      aria-valuenow={0}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Page scroll position"
      tabIndex={0}
    >
      <div
        ref={innerRef}
        className="relative w-full"
        style={{ height: lineHeight }}
      >
        {Array.from({ length: dashCount }, (_, i) => (
          <div
            key={i}
            className="absolute right-0"
            style={{
              top: `${verticalPadding + i * step}px`,
              width: baseDashWidth,
              height: dashHeight,
              backgroundColor: dashColor,
              transform: 'translateX(50%) scaleX(var(--scaleX, 1))',
              transformOrigin: 'center',
            }}
          />
        ))}
      </div>
    </div>
  )
}
